import React, { useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AutocompleteWithPagination from "../../../components/autocomplete-with-pagination";
import { productService } from "../../../utils/services/productService";
import { fieldSx } from "../constants";
import FieldLabel from "./FieldLabel";
import VariantsCell from "./VariantsCell";

const ProductEntry = ({
  entry,
  entryIndex,
  onProductChange,
  onVariantChange,
  onVariantsChange,
  onRemoveProduct,
  onPageChange,
  selectedClient,
}) => {
  const [expanded, setExpanded] = useState(true);

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      sx={{
        mb: 2,
        border: "1px solid var(--border-color)",
        borderRadius: 2,
        backgroundColor: "var(--bg-secondary, var(--bg-paper))",
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          backgroundColor: "var(--bg-paper)",
          borderBottom: expanded ? "1px solid var(--border-color)" : "none",
          "&:hover": { backgroundColor: "var(--bg-secondary)" },
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5} flex={1}>
          <Typography
            fontFamily="Poppins"
            fontWeight={500}
            fontSize="14px"
            color="var(--text-primary)"
          >
            {entry.product?.name || "Select a product"}
          </Typography>
          {entry.variants && entry.variants.length > 0 && (
            <Typography
              fontSize="12px"
              color="var(--text-tertiary)"
              fontFamily="Poppins"
            >
              ({entry.variants.length} variant
              {entry.variants.length !== 1 ? "s" : ""})
            </Typography>
          )}
        </Box>
        <Tooltip title="Remove product">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onRemoveProduct(entryIndex);
            }}
            sx={{ color: "error.main" }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </AccordionSummary>

      <AccordionDetails sx={{ p: 2 }}>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} md={2}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <FieldLabel>Page#</FieldLabel>
              <TextField
                size="small"
                fullWidth
                type="number"
                value={entry.page ?? ""}
                onChange={(e) => onPageChange(entryIndex, e.target.value)}
                sx={fieldSx}
                inputProps={{ min: 1, style: { fontSize: 12, height: "25px" } }}
                placeholder="Page number"
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={10}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <FieldLabel style={{ marginBottom: 0 }}>Product</FieldLabel>
              <AutocompleteWithPagination
                selectedItem={entry.product}
                onDataChange={(prod) => onProductChange(entryIndex, prod)}
                selectedClient={selectedClient}
                width="100%"
                optionKey="name"
                hideLabel
                backgroundColor="var(--bg-paper)"
                apiEndPoint={(limit, skip, search, client) =>
                  productService.fetchNames(search, client, limit, skip)
                }
              />
            </Box>
          </Grid>
        </Grid>

        {entry.product && (
          <Box sx={{ mt: 2 }}>
            <FieldLabel>Variants</FieldLabel>
            <VariantsCell
              variants={entry.variants}
              productSkus={entry.productSkus || []}
              onVariantChange={(vi, field, val) =>
                onVariantChange(entryIndex, vi, field, val)
              }
              onVariantsChange={(newVariants) =>
                onVariantsChange(entryIndex, newVariants)
              }
            />
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default ProductEntry;
